/**
 * "Create" of the new-chart wizard (W2-14 onboarding-wizard; DESIGN.md §4.3, §5.1 step 7):
 *
 *  1. assets: the song (as chosen, or its WAV conversion) and the illustration; a skipped Picture step gets the
 *     gradient placeholder generated from the title by the io worker, written as `illustration.png` (the path the
 *     checker's ST-02 tip recognises); a chosen picture keeps its own (sanitised) name, never that path;
 *  2. the document, built only through Core command bodies in one transaction over `Defaults.emptyDoc`: assets,
 *     song details (level string "IN Lv.13" plus the explicit difficulty, the preview range inside the song), offset
 *     (integer ms in META.offset, a remainder in info.yml through `setOffset`), timing confirmation (a skipped Beat
 *     step leaves it unconfirmed: tip ST-01) and the starting layout; the result must pass `checkInvariants`;
 *  3. `persist.createProject` (assets into OPFS, content-addressed, then the first snapshot into IndexedDB);
 *  4. afterwards (`openNewProject`): persistent storage requested on the first project (the Start step explains why
 *     beforehand; a refusal is reported with what to do), the Chart workspace opens and the tour is offered on the
 *     first chart.
 */

import type { AppServices, NewChartDraft } from '../../app/registry/types';
import { Core } from '../../core/commands';
import { buildDocWithCore, DocBuildError } from '../../core/doc/build';
import { Defaults } from '../../core/model/defaults';
import type { AssetId, AssetMeta, AssetRef, AssetRole, ChartDoc, ProjectId } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import { newRpeId } from '../../core/util/ids';
import type { MessageRef } from '../../core/util/types';
import { Names } from '../../formats/package/names';
import {
  CHARTER_EXT_KEY,
  DEFAULT_BPM,
  cleanDifficulty,
  cleanText,
  extOf,
  formatLevel,
  previewRangeProblem,
  stem,
  validBpm,
} from './draft';
import { metaOf, sha256Of, sniffFile } from './songAnalysis';
import { applyStartLayout, type StartLayoutNames } from './startLayouts';
import { m } from './wizard.messages';

/**
 * Package path of the generated placeholder illustration (DESIGN.md §5.1 step 3: "a gradient placeholder generated
 * from the title (written as illustration.png)"). The checker's ST-02 tip looks for exactly this path.
 */
export const PLACEHOLDER_ILLUSTRATION_PATH = 'illustration.png';
/** Size of the generated placeholder (16:9, Phira's reference resolution). */
export const PLACEHOLDER_SIZE = { width: 1920, height: 1080 } as const;

/** One asset of the new project: what persist stores plus the package path the document refers to. */
export interface NewAsset {
  readonly id: AssetId;
  readonly bytes: Uint8Array;
  readonly mime: string;
  readonly role: AssetRole;
  readonly meta: AssetMeta;
  readonly path: string;
}

export interface NewChartAssets {
  readonly music: NewAsset;
  readonly illustration: NewAsset;
}

/** Thrown when the draft cannot become a valid document (a bug or a stale draft; the dialog shows the message). */
export class NewChartError extends Error {
  readonly reason: MessageRef;
  constructor(reason: MessageRef, message: string) {
    super(message);
    this.name = 'NewChartError';
    this.reason = reason;
  }
}

/**
 * Runs `build` as one transaction over `doc` (core/doc/build.ts `buildDocWithCore`) and returns the resulting
 * document. A command failure or a violation throws NewChartError (with the user-facing reason).
 */
export function buildWithCore(doc: ChartDoc, build: (tx: TxBuilder) => void): ChartDoc {
  try {
    return buildDocWithCore(doc, build).doc;
  } catch (e) {
    if (!(e instanceof DocBuildError)) throw e;
    if (e.reason !== null) throw new NewChartError(e.reason, e.message);
    throw new NewChartError({ key: m.errorInvalid, params: { detail: e.violations[0]?.message ?? '' } }, e.message);
  }
}

/**
 * A package path for a user's file: its own name, sanitised (ASCII, safe for every unzip tool and for Phira),
 * unique among `taken`. Names without any ASCII letter or digit (e.g. Chinese titles) become `<fallback>.<ext>`.
 */
export function assetPathFor(fileName: string, fallback: string, taken: ReadonlySet<string>, role: AssetRole): string {
  const ext = extOf(fileName);
  const base = stem(fileName);
  const desired = /[A-Za-z0-9]/.test(base)
    ? `${base}${ext === '' ? '' : `.${ext}`}`
    : `${fallback}${ext === '' ? '' : `.${ext}`}`;
  return Names.sanitize(desired, taken, role);
}

/** Inputs of `buildNewChartDoc` besides the draft. */
export interface NewChartDocInput {
  readonly assets: NewChartAssets;
  readonly rpeId: string;
  readonly names: StartLayoutNames;
}

function assetRef(a: NewAsset): AssetRef {
  return { id: a.id, path: a.path, mime: a.mime, bytes: a.bytes.length, role: a.role, meta: a.meta };
}

/** The new chart's document (see the module header, step 2). */
export function buildNewChartDoc(draft: NewChartDraft, input: NewChartDocInput): ChartDoc {
  const music = draft.music;
  if (music === null) throw new NewChartError({ key: m.errorNoSong }, 'the draft has no song');
  const duration = music.durationSec;
  if (previewRangeProblem(draft.previewStart, draft.previewEnd, duration) !== null) {
    throw new NewChartError({ key: m.errorPreview }, 'the preview range is not inside the song');
  }
  const bpm = validBpm(draft.bpm) ? draft.bpm : DEFAULT_BPM;
  const empty = Defaults.emptyDoc({ name: cleanText(draft.name), bpm, offsetMs: 0, rpeId: input.rpeId });
  return buildWithCore(empty, (tx) => {
    Core.doc.addAsset(tx, { ref: assetRef(input.assets.music) });
    Core.doc.addAsset(tx, { ref: assetRef(input.assets.illustration) });
    Core.doc.patchMeta(tx, {
      patch: {
        composer: cleanText(draft.composer),
        illustrator: cleanText(draft.illustrator),
        charter: cleanText(draft.charter),
        level: formatLevel(draft.levelLabel, draft.difficulty),
        difficulty: cleanDifficulty(draft.difficulty),
        previewStart: draft.previewStart,
        previewEnd: draft.previewEnd,
        music: input.assets.music.id,
        illustration: input.assets.illustration.id,
      },
    });
    if (Number.isFinite(draft.offsetMs) && draft.offsetMs !== 0) Core.tempo.setOffset(tx, { ms: draft.offsetMs });
    if (draft.timingConfirmed) Core.tempo.setTimingConfirmed(tx, { confirmed: true });
    applyStartLayout(tx, draft.start, input.names);
  });
}

/** The draft's song as a project asset. */
async function musicAsset(
  services: Pick<AppServices, 'io'>,
  draft: NewChartDraft,
  taken: Set<string>,
): Promise<NewAsset> {
  const music = draft.music;
  if (music === null) throw new NewChartError({ key: m.errorNoSong }, 'the draft has no song');
  const sniff = sniffFile(music.bytes, music.name);
  const id = await sha256Of(services, music.bytes);
  const path = assetPathFor(music.name, 'music', taken, 'music');
  taken.add(path);
  const meta: AssetMeta = { ...metaOf(music.bytes, music.name), durationSec: music.durationSec };
  return { id, bytes: music.bytes, mime: sniff.mime, role: 'music', meta, path };
}

/** The chosen picture, or the generated placeholder when the Picture step was skipped. */
async function illustrationAsset(
  services: Pick<AppServices, 'io'>,
  draft: NewChartDraft,
  taken: Set<string>,
  untitled: string,
): Promise<NewAsset> {
  const pic = draft.illustration;
  if (pic !== null && !pic.placeholder) {
    const sniff = sniffFile(pic.bytes, pic.name);
    const id = await sha256Of(services, pic.bytes);
    const path = assetPathFor(pic.name, 'picture', new Set([...taken, PLACEHOLDER_ILLUSTRATION_PATH]), 'illustration');
    taken.add(path);
    return { id, bytes: pic.bytes, mime: sniff.mime, role: 'illustration', meta: metaOf(pic.bytes, pic.name), path };
  }
  const title = cleanText(draft.name) === '' ? untitled : cleanText(draft.name);
  const bytes =
    pic !== null
      ? pic.bytes
      : await services.io.call('placeholderIllustration', {
          title,
          width: PLACEHOLDER_SIZE.width,
          height: PLACEHOLDER_SIZE.height,
        });
  const id = await sha256Of(services, bytes);
  taken.add(PLACEHOLDER_ILLUSTRATION_PATH);
  return {
    id,
    bytes,
    mime: 'image/png',
    role: 'illustration',
    meta: metaOf(bytes, PLACEHOLDER_ILLUSTRATION_PATH),
    path: PLACEHOLDER_ILLUSTRATION_PATH,
  };
}

/** Song and illustration assets of a draft (placeholder generated when needed). */
export async function prepareAssets(
  services: Pick<AppServices, 'io'>,
  draft: NewChartDraft,
  untitled: string,
): Promise<NewChartAssets> {
  const taken = new Set<string>();
  const music = await musicAsset(services, draft, taken);
  const illustration = await illustrationAsset(services, draft, taken, untitled);
  return { music, illustration };
}

export interface CreateText {
  /** Title used for the placeholder of an untitled chart. */
  readonly untitled: string;
  readonly names: StartLayoutNames;
}

/** Steps 1-3 of the module header: assets, document, persisted project. Returns the new project's id. */
export async function createProjectFromDraft(
  services: Pick<AppServices, 'io' | 'persist' | 'settings'>,
  draft: NewChartDraft,
  text: CreateText,
): Promise<ProjectId> {
  const assets = await prepareAssets(services, draft, text.untitled);
  const doc = buildNewChartDoc(draft, { assets, rpeId: newRpeId(), names: text.names });
  const id = await services.persist.createProject(
    doc,
    [assets.music, assets.illustration].map((a) => ({
      id: a.id,
      bytes: a.bytes,
      mime: a.mime,
      role: a.role,
      meta: a.meta,
    })),
  );
  const charter = cleanText(draft.charter);
  if (services.settings.getExt(CHARTER_EXT_KEY) !== charter) services.settings.setExt(CHARTER_EXT_KEY, charter);
  return id;
}

/** True when no chart exists yet (the first project asks for persistent storage, DESIGN.md §5.1 step 7). */
export async function isFirstProject(services: Pick<AppServices, 'persist'>): Promise<boolean> {
  try {
    return (await services.persist.listProjects(true)).length === 0;
  } catch {
    return false;
  }
}

/** Step 4 of the module header (runs after the wizard closed). */
export async function openNewProject(
  services: AppServices,
  projectId: ProjectId,
  opts: { readonly firstProject: boolean },
): Promise<void> {
  if (opts.firstProject) {
    // UX-002 (CR-W3-01-7): no toast here — the shell's `notPersisted` banner/chip already says it (the toast only
    // repeated the live notice). Asking for persistence stays.
    await services.persist.requestPersistence().catch(() => undefined);
  }
  await services.nav.openProject(projectId, 'chart');
  const settings = services.settings.value;
  if (!settings.general.tourDone && services.registry.command('tour.start') !== undefined) {
    const start = await services.ui.confirm({
      title: { key: m.tourOfferTitle },
      body: { key: m.tourOfferBody },
      confirm: { key: m.tourOfferStart },
    });
    if (start) await services.registry.run('tour.start', undefined, 'button');
    else services.settings.set('general', 'tourDone', true);
  }
}
